import React, { useState } from 'react';
import { Box, Chip, Menu, MenuItem, Typography, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const MAX_SORT_COLUMNS = 3;

/**
 * Multi-column sort bar with draggable chips.
 *
 * @param {Array} sortColumns - [{ field, direction: 'asc'|'desc' }]
 * @param {function} onSortChange - callback with new sortColumns array
 * @param {Array} sortableFields - [{ value, label }] available sort fields
 */
export default function SortChipBar({ sortColumns = [], onSortChange, sortableFields = [] }) {
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [dragItem, setDragItem] = useState(null);

  const getFieldLabel = (field) => {
    const f = sortableFields.find(f => f.value === field);
    return f ? f.label : field;
  };

  // Simple drag reorder using native drag events (no react-beautiful-dnd dependency)
  const handleDragStart = (index) => { setDragItem(index); };
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragItem === null || dragItem === index) return;
    const reordered = Array.from(sortColumns);
    const [removed] = reordered.splice(dragItem, 1);
    reordered.splice(index, 0, removed);
    setDragItem(index);
    onSortChange(reordered);
  };
  const handleDragEnd = () => { setDragItem(null); };

  const handleToggleDirection = (index, event) => {
    event.stopPropagation();
    const newColumns = [...sortColumns];
    newColumns[index] = { ...newColumns[index], direction: newColumns[index].direction === 'asc' ? 'desc' : 'asc' };
    onSortChange(newColumns);
  };

  const handleRemove = (index, event) => {
    event.stopPropagation();
    onSortChange(sortColumns.filter((_, i) => i !== index));
  };

  const handleAddSort = (field) => {
    setAddMenuAnchor(null);
    const existingIndex = sortColumns.findIndex(col => col.field === field);
    if (existingIndex >= 0) {
      const newColumns = sortColumns.filter((_, i) => i !== existingIndex);
      newColumns.push({ field, direction: sortColumns[existingIndex].direction === 'asc' ? 'desc' : 'asc' });
      onSortChange(newColumns);
    } else {
      onSortChange([...sortColumns, { field, direction: 'asc' }]);
    }
  };

  const availableFields = sortableFields.filter(f => !sortColumns.some(col => col.field === f.value));

  if (sortColumns.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1, minHeight: 40 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.8rem', mr: 0.5 }}>
        Sort:
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        {sortColumns.map((col, index) => (
          <Chip
            key={col.field}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            size="small"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <DragIndicatorIcon sx={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.4)', cursor: 'grab', mr: 0.25 }} />
                <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1A2B4A' }}>
                  {index + 1}.
                </Typography>
                <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                  {getFieldLabel(col.field)}
                </Typography>
                <Tooltip title={`Sort ${col.direction === 'asc' ? 'ascending' : 'descending'} - click to toggle`}>
                  <IconButton size="small" onClick={(e) => handleToggleDirection(index, e)}
                    aria-label={`Sort ${getFieldLabel(col.field)} ${col.direction === 'asc' ? 'descending' : 'ascending'} - click to toggle`}
                    sx={{ p: 0, ml: 0.25, '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}>
                    {col.direction === 'asc'
                      ? <ArrowUpwardIcon sx={{ fontSize: 16, color: '#1A2B4A' }} />
                      : <ArrowDownwardIcon sx={{ fontSize: 16, color: '#1A2B4A' }} />}
                  </IconButton>
                </Tooltip>
              </Box>
            }
            onDelete={(e) => handleRemove(index, e)}
            sx={{
              backgroundColor: dragItem === index ? 'rgba(25, 118, 210, 0.15)' : 'rgba(0, 34, 68, 0.08)',
              borderColor: dragItem === index ? '#1A2B4A' : 'transparent',
              border: '1px solid',
              '& .MuiChip-deleteIcon': { fontSize: 16, color: 'rgba(0, 0, 0, 0.4)', '&:hover': { color: '#8B2635' } },
              '& .MuiChip-label': { px: 0.5 }
            }}
          />
        ))}
      </Box>

      {sortColumns.length < MAX_SORT_COLUMNS && availableFields.length > 0 && (
        <>
          <Tooltip title="Add sort column">
            <Chip size="small" icon={<AddIcon sx={{ fontSize: 16 }} />} label="Add"
              onClick={(e) => setAddMenuAnchor(e.currentTarget)} variant="outlined"
              sx={{
                cursor: 'pointer', borderStyle: 'dashed', borderColor: 'rgba(0, 0, 0, 0.23)',
                '& .MuiChip-label': { px: 0.5, fontSize: '0.8rem' },
                '& .MuiChip-icon': { ml: 0.5, color: 'inherit' },
                '&:hover': { borderColor: '#1A2B4A', backgroundColor: 'rgba(25, 118, 210, 0.04)' }
              }} />
          </Tooltip>
          <Menu anchorEl={addMenuAnchor} open={Boolean(addMenuAnchor)} onClose={() => setAddMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} PaperProps={{ sx: { maxHeight: 300 } }}>
            {availableFields.map(field => (
              <MenuItem key={field.value} onClick={() => handleAddSort(field.value)} sx={{ fontSize: '0.875rem' }}>
                {field.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
}

export { MAX_SORT_COLUMNS };
